import React, { useState, useEffect } from 'react';
import { useAnalytics } from './AdvancedAnalyticsProvider';
import { Grip, Plus, Settings, Trash2, Eye, EyeOff, Save, Layout, Grid, Columns, Rows } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

interface Widget {
  id: string;
  type: 'chart' | 'stats' | 'table' | 'heatmap' | 'trend';
  title: string;
  chartType?: 'line' | 'bar' | 'doughnut' | 'radar' | 'scatter';
  metric?: string;
  visible: boolean;
  size: 'small' | 'medium' | 'large';
  position: number;
}

interface DashboardLayout {
  columns: number;
  widgets: Widget[];
}

const initialWidgets: Widget[] = [
  {
    id: 'revenue-trend',
    type: 'chart',
    title: 'Revenue Trend',
    chartType: 'line',
    metric: 'revenue',
    visible: true,
    size: 'large',
    position: 0
  },
  {
    id: 'activity-breakdown',
    type: 'chart',
    title: 'Activity Breakdown',
    chartType: 'bar',
    visible: true,
    size: 'medium',
    position: 1
  },
  {
    id: 'performance-metrics',
    type: 'stats',
    title: 'Performance Metrics',
    visible: true,
    size: 'small',
    position: 2
  },
  {
    id: 'member-growth',
    type: 'chart',
    title: 'Member Growth',
    chartType: 'line',
    metric: 'members',
    visible: true,
    size: 'medium',
    position: 3
  },
  {
    id: 'activity-heatmap',
    type: 'heatmap',
    title: 'Activity Heatmap',
    visible: true,
    size: 'large',
    position: 4
  }
];

export const DashboardCustomizer: React.FC = () => {
  const { filters } = useAnalytics();
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [layout, setLayout] = useState<DashboardLayout>({
    columns: 2,
    widgets: initialWidgets
  });
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Load saved layout from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('dashboard_layout');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setLayout(parsed);
      } catch (error) {
        console.error('Failed to parse saved layout:', error);
      }
    }
  }, []);

  // Save layout to localStorage
  useEffect(() => {
    if (unsavedChanges) {
      localStorage.setItem('dashboard_layout', JSON.stringify(layout));
      setUnsavedChanges(false);
    }
  }, [layout, unsavedChanges]);

  const toggleWidgetVisibility = React.useCallback((widgetId: string) => {
    setLayout(prev => ({
      ...prev,
      widgets: prev.widgets.map(widget =>
        widget.id === widgetId
          ? { ...widget, visible: !widget.visible }
          : widget
      )
    }));
    setUnsavedChanges(true);
  }, []);

  const removeWidget = React.useCallback((widgetId: string) => {
    setLayout(prev => ({
      ...prev,
      widgets: prev.widgets
        .filter(widget => widget.id !== widgetId)
        .map((widget, index) => ({ ...widget, position: index }))
    }));
    setUnsavedChanges(true);
  }, []);

  const addWidget = React.useCallback((widgetType: Widget['type']) => {
    const newWidget: Widget = {
      id: `${widgetType}-${Date.now()}`,
      type: widgetType,
      title: `${widgetType.charAt(0).toUpperCase() + widgetType.slice(1)} Widget`,
      visible: true,
      size: 'medium',
      position: layout.widgets.length
    };

    if (widgetType === 'chart') {
      newWidget.chartType = 'line';
      newWidget.metric = 'revenue';
    }

    setLayout(prev => ({
      ...prev,
      widgets: [...prev.widgets, newWidget]
    }));
    setUnsavedChanges(true);
  }, [layout.widgets.length]);

  const onDragEnd = React.useCallback((result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(layout.widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedWidgets = items.map((widget, index) => ({
      ...widget,
      position: index
    }));

    setLayout(prev => ({
      ...prev,
      widgets: updatedWidgets
    }));
    setUnsavedChanges(true);
  }, [layout.widgets]);

  const updateWidgetSize = React.useCallback((widgetId: string, size: Widget['size']) => {
    setLayout(prev => ({
      ...prev,
      widgets: prev.widgets.map(widget =>
        widget.id === widgetId ? { ...widget, size } : widget
      )
    }));
    setUnsavedChanges(true);
  }, []);

  const getWidgetClass = (widget: Widget) => {
    const baseClass = 'bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6 transition-all duration-200';
    const sizeClass = widget.size === 'large' ? 'lg:col-span-2' :
      widget.size === 'medium' ? 'lg:col-span-1' : 'lg:col-span-1';
    const visibilityClass = widget.visible ? 'opacity-100' : 'opacity-50';

    return `${baseClass} ${sizeClass} ${visibilityClass}`;
  };

  return (
    <div className="space-y-6">
      {/* Customizer Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Layout className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Dashboard Customizer</h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {layout.widgets.filter(w => w.visible).length} visible widgets
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setIsCustomizing(!isCustomizing)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isCustomizing
              ? 'bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
              : 'bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700'
              }`}
          >
            <Settings className="h-4 w-4" />
            <span>{isCustomizing ? 'Exit Customizer' : 'Customize Dashboard'}</span>
          </button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-300">Columns:</span>
            <select
              value={layout.columns}
              onChange={(e) => setLayout(prev => ({ ...prev, columns: Number(e.target.value) }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>

          <button
            onClick={() => {
              localStorage.removeItem('dashboard_layout');
              setLayout({ columns: 2, widgets: initialWidgets });
              setUnsavedChanges(true);
            }}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            <span>Reset</span>
          </button>

          <button
            onClick={() => setUnsavedChanges(true)}
            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Save className="h-4 w-4" />
            <span>Save</span>
          </button>
        </div>
      </div>

      {/* Widget Management */}
      {isCustomizing && (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary">Manage Widgets</h4>
            <div className="flex items-center gap-2">
              <button
                onClick={() => addWidget('chart')}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Chart</span>
              </button>
              <button
                onClick={() => addWidget('stats')}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Stats</span>
              </button>
              <button
                onClick={() => addWidget('heatmap')}
                className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Heatmap</span>
              </button>
            </div>
          </div>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="widgets" direction="vertical">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-4"
                >
                  {layout.widgets.map((widget, index) => (
                    <Draggable key={widget.id} draggableId={widget.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`border-2 ${snapshot.isDragging ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700'
                            } rounded-lg p-4`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div {...provided.dragHandleProps} className="cursor-move">
                                <Grip className="h-4 w-4 text-gray-400" />
                              </div>
                              <div>
                                <h5 className="font-medium text-gray-900 dark:text-dark-text-primary">{widget.title}</h5>
                                <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{widget.type}</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <select
                                value={widget.size}
                                onChange={(e) => updateWidgetSize(widget.id, e.target.value as Widget['size'])}
                                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary text-sm"
                              >
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                              </select>

                              <button
                                onClick={() => toggleWidgetVisibility(widget.id)}
                                className={`p-2 rounded ${widget.visible
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400'
                                  }`}
                                title={widget.visible ? 'Hide Widget' : 'Show Widget'}
                              >
                                {widget.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                              </button>

                              <button
                                onClick={() => removeWidget(widget.id)}
                                className="p-2 rounded bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/30 transition-colors"
                                title="Remove Widget"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}

      {/* Preview Grid */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary">Preview</h4>
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Grid className="h-4 w-4" />
            <span>{layout.columns} columns</span>
          </div>
        </div>

        <div className={`grid gap-6 ${layout.columns === 1 ? 'grid-cols-1' : layout.columns === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
          {layout.widgets
            .filter(widget => widget.visible)
            .sort((a, b) => a.position - b.position)
            .map(widget => (
              <div key={widget.id} className={getWidgetClass(widget)}>
                <div className="flex items-center justify-between mb-4">
                  <h5 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary">{widget.title}</h5>
                  <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{widget.type}</span>
                </div>

                {widget.type === 'chart' && widget.chartType && (
                  <div className="h-64 bg-gray-50 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Chart: {widget.chartType}</p>
                  </div>
                )}

                {widget.type === 'stats' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <p className="text-sm text-blue-600 dark:text-blue-400">Revenue</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">GH₵0</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                      <p className="text-sm text-green-600 dark:text-green-400">Members</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">0</p>
                    </div>
                  </div>
                )}

                {widget.type === 'heatmap' && (
                  <div className="h-64 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Activity Heatmap</p>
                    <div className="grid grid-cols-7 gap-2">
                      {Array.from({ length: 49 }).map((_, i) => (
                        <div
                          key={i}
                          className="h-8 bg-gray-300 dark:bg-gray-600 rounded opacity-50 hover:opacity-100 transition-opacity"
                          style={{ opacity: Math.random() }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};